import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { money } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Clock, Download, MessageCircle, XCircle } from "lucide-react";

export default function OrderConfirmation() {
  const { orderNo } = useParams();
  const [params] = useSearchParams();
  const payStatus = params.get("status") || "pending";
  const [order, setOrder] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!orderNo) return;
    api
      .get(`/orders/track/${orderNo}`)
      .then(({ data }) => setOrder(data))
      .catch((e) => setError(e?.response?.data?.detail || "Order not found"));
  }, [orderNo]);

  if (error) {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6">
        <div className="text-center">
          <XCircle className="h-10 w-10 mx-auto opacity-60" />
          <p className="mt-4 opacity-70">{error}</p>
          <Link to="/shop" className="mt-6 inline-block label-xs link-underline">
            Continue shopping
          </Link>
        </div>
      </div>
    );
  }
  if (!order) return <div className="min-h-[60vh] grid place-items-center label-xs">Loading…</div>;

  const paid = payStatus === "paid" || order.payment_status === "advance_paid" || order.payment_status === "fully_paid";
  const isCod = payStatus === "cod" || order.payment_method === "cod";
  const badge =
    isCod ? { Icon: CheckCircle2, text: "Order confirmed — pay cash on delivery", tone: "border-foreground" } :
    paid ? { Icon: CheckCircle2, text: "Advance received", tone: "border-foreground" } :
    payStatus === "cancelled" ? { Icon: Clock, text: "Payment cancelled — order pending", tone: "border-foreground/40" } :
    payStatus === "verify_failed" ? { Icon: XCircle, text: "Payment verification failed", tone: "border-foreground" } :
    { Icon: Clock, text: "Awaiting payment", tone: "border-foreground/40" };

  const apiBase = api.defaults.baseURL || "";
  const invoiceUrl = order.download_token
    ? `${apiBase}/orders/${order.order_no}/invoice.pdf?t=${order.download_token}`
    : `${apiBase}/orders/${order.order_no}/invoice.pdf`;

  return (
    <div className="max-w-3xl mx-auto px-6 py-16" data-testid="order-confirmation-page">
      <div className="text-center">
        <p className="label-xs opacity-60">Order Confirmation</p>
        <h1 className="mt-3 text-5xl font-serif tracking-tighter">
          {isCod ? "Order confirmed." : paid ? "Thank you." : "Order received."}
        </h1>
        <p className="mt-4 opacity-70">
          Order <span className="text-foreground" data-testid="confirmation-order-no">{order.order_no}</span> — {new Date(order.created_at).toLocaleString()}
        </p>
        <div className={`mt-8 inline-flex items-center gap-2 border ${badge.tone} px-4 py-2 label-xs`} data-testid="payment-badge">
          <badge.Icon className="h-3.5 w-3.5" />
          {badge.text}
        </div>
      </div>

      {/* Payment split */}
      <div className="mt-12 border p-6" data-testid="confirmation-summary">
        <p className="label-xs">Payment Summary</p>
        <div className="mt-4 grid grid-cols-2 gap-6">
          <div>
            <p className="label-xs opacity-60">Grand Total</p>
            <p className="text-2xl font-body mt-1">{money(order.total)}</p>
          </div>
          <div>
            <p className="label-xs opacity-60">Advance ({order.advance_percent}%)</p>
            <p className="text-2xl font-body mt-1">{money(order.advance_amount)}</p>
            <p className="text-xs opacity-60 mt-1">{order.advance_payment_status === "paid" ? "Paid" : "Pending"}</p>
          </div>
        </div>
        <div className="mt-4 border-t pt-4 flex justify-between text-sm">
          <span>Remaining due</span>
          <span className="font-body">{money(order.remaining_amount)}</span>
        </div>
      </div>

      {/* Items */}
      <div className="mt-8">
        <p className="label-xs mb-3">Items</p>
        <div className="border divide-y">
          {(order.items || []).map((it, i) => (
            <div key={i} className="p-4 flex gap-3 items-center">
              {it.image && <img src={it.image} alt="" className="w-14 h-16 object-cover" />}
              <div className="flex-1">
                <p className="text-sm">{it.name}</p>
                <p className="text-xs opacity-60">Size {it.size} · Qty {it.qty}</p>
              </div>
              <p className="text-sm">{money((it.line_total ?? (it.price || 0) * (it.qty || 1)))}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Actions */}
      <div className="mt-10 flex flex-wrap gap-3 justify-center">
        <a href={invoiceUrl} target="_blank" rel="noreferrer" data-testid="download-invoice">
          <Button variant="outline" className="rounded-none border-foreground label-xs gap-2 h-12 px-6">
            <Download className="h-3.5 w-3.5" />
            Download Invoice
          </Button>
        </a>
        <Link to={`/track/${order.order_no}`}>
          <Button className="rounded-none label-xs h-12 px-6" data-testid="track-order-btn">
            Track Order
          </Button>
        </Link>
        <Link to="/shop">
          <Button variant="outline" className="rounded-none border-foreground label-xs h-12 px-6">
            Continue Shopping
          </Button>
        </Link>
      </div>

      <p className="mt-12 text-center text-xs opacity-60">
        Questions? Message us on{" "}
        <a href="https://wa.me/919729927206" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 link-underline">
          <MessageCircle className="h-3 w-3" /> WhatsApp
        </a>
      </p>
    </div>
  );
}
